import { PaymentElement } from '@stripe/react-stripe-js'
import React, { useEffect, useState } from 'react'
import { User, Product, Campaign } from 'app/types/authorization'
import { useTranslation } from 'next-i18next'
import { CheckoutResponse } from 'app/types/checkout'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { CompanyData, CompanyProfile } from 'app/types/organization'
import { Billing, BillingNoStripe } from './Billing'
import Currency from 'app/types/currency'

// Props for the InnerCheckoutForm component
interface InnerCheckoutFormProps {
  user: User | undefined
  checkoutSession: CheckoutResponse
}

// Component for rendering the inner checkout form with Stripe PaymentElement
const InnerCheckoutForm: React.FC<InnerCheckoutFormProps> = () => {
  return (
    <div>
      <div className="mt-6 grid grid-cols-3 gap-x-4 gap-y-6 sm:grid-cols-4">
        <div className="col-span-3 sm:col-span-4">
          <PaymentElement />
        </div>
      </div>
    </div>
  )
}

// Props for the StripeCheckoutForm component
interface StripeCheckoutFormProps {
  user: User | undefined
  checkoutSession: CheckoutResponse
  setCheckoutSession: React.Dispatch<
    React.SetStateAction<CheckoutResponse | undefined>
  >
}

// Component for rendering the Stripe checkout form
export const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = ({
  user,
  checkoutSession,
}) => {
  const { t } = useTranslation('common')

  return (
    <div className="mt-10">
      <h3 id="payment-heading" className="text-lg font-medium text-gray-900">
        {t('payment-details')}
      </h3>
      <InnerCheckoutForm checkoutSession={checkoutSession} user={user} />
    </div>
  )
}

// Initialize Stripe promise
const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ??
    'pk_live_51Ln7WRJq5BvuWI4h3Ix6v5jGNlur6zf90TesGPyHpoW3g5YGuqkFZriyfQQ00kWdPGhYUcjH7zUNzniyGOOtsJvx00HvtTz2sN'
)

// Props for the CheckoutForm component
export interface CheckoutFormProps {
  user: User | undefined
  checkoutSession: CheckoutResponse
  eventSk: string
  organization: string
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  setCheckoutSession: React.Dispatch<
    React.SetStateAction<CheckoutResponse | undefined>
  >
  items: Product[]
  campaigns: Campaign[]
  companyProfile: CompanyProfile | null
  companyData: CompanyData[]
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => Promise<void>
  name?: string
  email?: string
  phoneNumber?: string
  poolId?: string
  shippingMethod?: 'pickup' | 'shipping'
}

// Main CheckoutForm component
export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  user,
  checkoutSession,
  eventSk,
  organization,
  setShowSuccessMessage,
  setShowFailureMessage,
  setCheckoutSession,
  items,
  campaigns,
  companyProfile,
  companyData,
  setCurrentStep,
  refetchCart,
  name,
  email,
  phoneNumber,
  poolId,
  shippingMethod,
}) => {
  const [show, setShow] = useState<boolean>(false)

  // Stripe options
  const options = {
    clientSecret: checkoutSession.stripe_client_secret ?? '',
  }

  // Show component after initial render
  useEffect(() => {
    setShow(true)
  }, [])

  if (!show) {
    return null
  }

  // Render BillingNoStripe component if Stripe client secret is not available
  if (checkoutSession.stripe_client_secret === null) {
    return (
      <BillingNoStripe
        user={user}
        checkoutSession={checkoutSession}
        eventSk={eventSk}
        setShowSuccessMessage={setShowSuccessMessage}
        setShowFailureMessage={setShowFailureMessage}
        setCheckoutSession={setCheckoutSession}
        items={items}
        campaigns={campaigns}
        companyProfile={companyProfile}
        companyData={companyData}
        organization={organization}
        setCurrentStep={setCurrentStep}
        refetchCart={refetchCart}
        existingName={name}
        existingEmail={email}
        existingPhoneNumber={phoneNumber}
        currency={
          checkoutSession.checkout_session.cart.currency ?? Currency.SEK
        }
        poolId={poolId}
        shippingMethod={shippingMethod}
      />
    )
  }

  // Render Stripe Elements with Billing component
  return (
    <Elements stripe={stripePromise} options={options}>
      <Billing
        user={user}
        checkoutSession={checkoutSession}
        eventSk={eventSk}
        setShowSuccessMessage={setShowSuccessMessage}
        setShowFailureMessage={setShowFailureMessage}
        setCheckoutSession={setCheckoutSession}
        items={items}
        campaigns={campaigns}
        companyProfile={companyProfile}
        companyData={companyData}
        organization={organization}
        setCurrentStep={setCurrentStep}
        refetchCart={refetchCart}
        existingName={name}
        existingEmail={email}
        existingPhoneNumber={phoneNumber}
        currency={
          checkoutSession.checkout_session.cart.currency ?? Currency.SEK
        }
        poolId={poolId}
        shippingMethod={shippingMethod}
      />
    </Elements>
  )
}
